import React, { useState, useCallback, useEffect } from 'react';
import { Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { GRID_CONFIG } from '../gridConfig';
import type { MilestoneMap1, MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import type { Objective1 } from '@mm-shared/types/Objective1';

import { getIntervalsForMilestoneMap } from '@mm-shared/intervals';
import { MapNodeData } from './MapNode';
import { getOrderedObjectives } from '../../utils/objective-utils';
import {
  getMapYOffsets,
  ColumnInfo,
  isStatusColumn,
  calculateHeightByRows,
  getMapYOffsetsWithFullyCollapsedGrouping,
} from '../utils/layoutMath';
import { type DependencyRecord } from '@mm-shared/dependencies';
import { Map3UISettings } from '../types';
import './Map3Relatives.css';
import { getStackedColumnsInfo } from '../utils/layoutMath.Stacked';
import {
  getSharedTimelineColumnsInfo,
  getMapSharedTimelineXOffsetForMap,
} from '../utils/layoutMath.SharedTimeline';
import { getUniqueIntervals } from '@mm-shared/intervals';
import { type UserMapCollapseControls } from './FullyCollapsedMapsGroup';
import { Map3RelativesSharedTimeline } from './Map3RelativesSharedTimeline';
import { Map3RelativesStacked } from './Map3RelativesStacked';

export interface Map3RelativesProps {
  mainMilestoneMapUuid: string;
  relatedMilestoneMaps: MilestoneMap1[];
  allObjectives: Objective1[];
  dependencyRecords: DependencyRecord[];
  children?: React.ReactNode;
  queueResultDeadlineUpdate: (resultUuid: string, newDeadline: string) => void;
  queueDependencyAdd: (sourceResultUuid: string, targetResultUuid: string) => void;
  queueDependencyRemove: (sourceResultUuid: string, targetResultUuid: string) => void;
  settings?: Map3UISettings;
  viewMode?: 'stacked' | 'sharedTimeline';
  zoomLevel?: MilestoneMapInterval;
  objectiveDisplayMode?: 'show_all' | 'show_with_dependencies' | 'show_all_relatives';
  canEdit: boolean;
}

/**
 * RelativesMapData serves as the single source of truth for all data and layout information
 * needed to render one or more milestone maps in React Flow.
 *
 * This includes:
 * - Base data (milestoneMap, objectives)
 * - Interval information for the active view mode relative to both the
 *   React Flow master grid and rows, columns, and intervals relative to
 *   each specific map
 * - Column layout information (width, position, dates)
 * - Map positioning on the master grid
 */
export interface RelativesMapData {
  milestoneMap: MilestoneMap1;
  objectives: Objective1[];
  // Milestone map's default intervals
  defaultIntervals: Array<{ start: Date; end: Date }>;
  // Milestone map's adjusted intervals (zoomLevel)
  adjustedIntervals: Array<{ start: Date; end: Date }>;
  // Master and map-level column layout information
  columnsInfo: ColumnInfo[];
  // Map positioning on the master grid
  mapXOffset: number;
  mapYOffset: number;
  // Map collapse/expand state (embedded instead of separate mapStates object)
  collapseLevel: 'expanded' | 'titleOnly' | 'fullyCollapsed';
  hideRowsWithoutDependencies: boolean;
  heightByRows: number;
}

/**
 * Map collapse/expand state for individual maps
 */
export interface MapCollapseState {
  collapseLevel: 'expanded' | 'titleOnly' | 'fullyCollapsed';
  hideRowsWithoutDependencies: boolean;
  heightByRows: number;
}

/**
 * Base props for Map3RelativesStacked and Map3RelativesSharedTimeline
 */
export interface Map3RelativesBaseProps extends Omit<Map3RelativesProps, 'viewMode'> {
  relativesMapsData: RelativesMapData[];
  updateMapState: (mapUuid: string, updates: UserMapCollapseControls) => void;
  canEdit: boolean;
}

/**
 * Creates the single source of truth for all map data and layout information.
 * This function is responsible for:
 * 1. Organizing the base data (maps, objectives)
 * 2. Calculating intervals for different view modes
 * 3. Determining column layout and positioning
 * 4. Computing map offsets for the master grid
 * 5. **Always returns DEFAULT collapse state** (expanded, no hidden rows, full height)
 *
 * All visual components (map nodes, result nodes, etc.) should derive
 * their positioning and layout information from the RelativesMapData returned by
 * this function to ensure consistency.
 *
 * NOTE: This function does NOT preserve collapse state across calls. If you need
 * to maintain user's collapse/expand choices, you must handle state preservation
 * at the caller level (see Map3Relatives component for example).
 */
function getRelativesMapsData(
  relatedMilestoneMaps: MilestoneMap1[],
  allObjectives: Objective1[],
  viewMode: 'stacked' | 'sharedTimeline' = 'stacked',
  zoomLevel?: MilestoneMapInterval,
): RelativesMapData[] {
  const data: RelativesMapData[] = [];

  // Get y-offsets first as we need them for all maps
  const maps: Array<{
    milestoneMap: MilestoneMap1;
    objectives: Objective1[];
    defaultIntervals: Array<{ start: Date; end: Date }>;
    adjustedIntervals: Array<{ start: Date; end: Date }>;
  }> = relatedMilestoneMaps.map((m) => {
    const defaultIntervals = getIntervalsForMilestoneMap(m);
    const adjustedIntervals = zoomLevel
      ? getUniqueIntervals(defaultIntervals, zoomLevel)
      : defaultIntervals;
    return {
      milestoneMap: m,
      objectives: getOrderedObjectives(m, allObjectives),
      defaultIntervals,
      adjustedIntervals,
    };
  });

  const yOffsets = getMapYOffsets(maps);

  maps.forEach((map, index) => {
    const { milestoneMap, objectives, defaultIntervals } = map;

    // Calculate columnsInfo based on view mode
    let columnsInfo: ColumnInfo[] = [];
    let adjustedIntervals: Array<{ start: Date; end: Date }> = [];
    let mapXOffset: number = 0;

    if (viewMode === 'stacked') {
      // For stacked view, generate columns info for this specific map
      columnsInfo = getStackedColumnsInfo(defaultIntervals, milestoneMap.interval);
      mapXOffset = 0; // In stacked view, all maps are left-aligned
    } else if (viewMode === 'sharedTimeline' && zoomLevel) {
      adjustedIntervals = getUniqueIntervals(defaultIntervals, zoomLevel);
      // For shared timeline view, we need all maps' data to calculate unified columns
      // Pass adjustedIntervals since we're in shared timeline mode.
      // Note: getSharedTimelineColumnsInfo will actually convert defaultIntervals too.
      // Might as well pass adjustedIntervals though since it's already calculated.
      const mapsWithAdjustedIntervals = maps.map((m) => ({
        ...m,
        intervals: m.adjustedIntervals,
      }));
      columnsInfo = getSharedTimelineColumnsInfo(
        mapsWithAdjustedIntervals,
        zoomLevel,
        milestoneMap.uuid,
      );
      mapXOffset = getMapSharedTimelineXOffsetForMap(columnsInfo);
    }

    data.push({
      milestoneMap,
      objectives,
      defaultIntervals,
      adjustedIntervals,
      columnsInfo,
      mapXOffset,
      mapYOffset: yOffsets[index],
      // DEFAULT collapse state - always the same regardless of previous user choices
      // The Map3Relatives component is responsible for preserving state across updates
      collapseLevel: 'expanded',
      hideRowsWithoutDependencies: false,
      heightByRows: objectives.length, // Initialize with actual objective count
    });
  });

  return data;
}

/**
 * Thin wrapper component that handles the view mode and map states
 *
 * TODO: Move getRelativesMapsData to this component. Processing relatedMilestoneMaps
 * to get the relativesMapsData seems like it should be done here and would simplify
 * the Map3RelativesStacked and Map3RelativesSharedTimeline components. We could also cache
 * the relativesMapsData and use it to avoid recomputing it when the view mode or zoom level changes.
 */
export function Map3Relatives(props: Map3RelativesProps) {
  const {
    viewMode = 'sharedTimeline',
    zoomLevel,
    relatedMilestoneMaps,
    mainMilestoneMapUuid,
    canEdit,
    allObjectives,
    ...rest
  } = props;

  // State for relativesMapsData with embedded collapse state
  const [relativesMapsData, setRelativesMapsData] = useState<RelativesMapData[]>([]);

  // Helper function to get default state for a map
  function getDefaultMapState(): MapCollapseState {
    return {
      collapseLevel: 'expanded',
      hideRowsWithoutDependencies: false,
      heightByRows: 1, // Default to showing the title bar if no height is provided.
    };
  }

  // Update map state callback - updates embedded state in relativesMapsData
  // This is called when user clicks collapse/expand buttons in MapNode title bar
  const updateMapState = useCallback(
    (mapUuid: string, updates: UserMapCollapseControls) => {
      setRelativesMapsData((prev) => updateMapState_(prev, mapUuid, updates, mainMilestoneMapUuid));
    },
    [mainMilestoneMapUuid],
  );

  // Calculate relativesMapsData once at the top level
  // This effect handles view mode switching and ensures proper layout recalculation
  useEffect(() => {
    const mainMap = relatedMilestoneMaps.find((m) => m.uuid === mainMilestoneMapUuid);
    if (!mainMap) {
      // Defensive guard: skip state update during initial render race condition
      // This can happen when relatedMilestoneMaps is empty during initial Redux state hydration
      console.warn(
        `[Map3Relatives] Main map not found in useEffect, skipping state update. uuid: ${mainMilestoneMapUuid}, available maps: ${relatedMilestoneMaps.length}`,
      );
      return;
    }

    // Sort related milestone maps to ensure main map is first
    const sortedRelatedMilestoneMaps = [
      mainMap,
      ...relatedMilestoneMaps.filter((m) => m.uuid !== mainMilestoneMapUuid),
    ];

    // STEP 1: Generate fresh RelativesMapData with DEFAULT collapse state
    // This function always returns maps with:
    // - collapseLevel: 'expanded'
    // - hideRowsWithoutDependencies: false
    // - heightByRows: objectives.length
    const newRelativesMapsData = getRelativesMapsData(
      sortedRelatedMilestoneMaps,
      allObjectives,
      viewMode,
      zoomLevel,
    );

    // STEP 2: Preserve existing collapse state when updating data
    // This is needed because getRelativesMapsData() always returns default state,
    // but we want to maintain user's collapse/expand choices across data updates
    setRelativesMapsData((prev) => {
      // STEP 2a: Extract existing collapse state from previous RelativesMapData
      // Create a lookup map: mapUuid -> {collapseLevel, hideRowsWithoutDependencies, heightByRows}
      const prevStateByUuid = new Map(
        prev.map((data) => [
          data.milestoneMap.uuid,
          {
            collapseLevel: data.collapseLevel,
            hideRowsWithoutDependencies: data.hideRowsWithoutDependencies,
            heightByRows: data.heightByRows,
          },
        ]),
      );

      // STEP 2b: Merge new layout data with preserved collapse state
      const mapsWithRestoredState = newRelativesMapsData.map((newData) => {
        // Try to find existing collapse state for this map UUID
        const existingState = prevStateByUuid.get(newData.milestoneMap.uuid);

        if (existingState) {
          // PRESERVE: Map existed before, restore its previous collapse state
          // Keep new layout data (positions, columns, etc.) but restore collapse state
          const mapWithRestoredState = {
            ...newData,
            // Explicitly preserve only the collapse-related properties
            collapseLevel: existingState.collapseLevel,
            hideRowsWithoutDependencies: existingState.hideRowsWithoutDependencies,
            // DON'T preserve heightByRows - recalculate for new view mode
          };

          // Recalculate heightByRows based on the new view mode's requirements
          const recalculatedHeight = calculateHeightByRows(mapWithRestoredState);

          return {
            ...mapWithRestoredState,
            heightByRows: recalculatedHeight,
          };
        } else {
          // NEW MAP: Use default collapse state from getRelativesMapsData
          // This happens when a new map is added to the view
          return newData;
        }
      });

      // STEP 2c: Recalculate Y-offsets since heights may have changed
      // This ensures proper positioning when switching between view modes
      const newYOffsets = getMapYOffsetsWithFullyCollapsedGrouping(
        mapsWithRestoredState,
        mainMilestoneMapUuid,
      );

      // STEP 2d: Apply new Y-offsets to all maps
      return mapsWithRestoredState.map((mapData, index) => ({
        ...mapData,
        mapYOffset: newYOffsets[index],
      }));
    });

    // This effect runs when:
    // - Maps are added/removed (relatedMilestoneMaps changes)
    // - View mode changes (stacked <-> sharedTimeline)
    // - Zoom level changes (affects column layout)
    // - Main map changes
    // In all cases, we want to preserve collapse state while updating layout
  }, [mainMilestoneMapUuid,
    canEdit, relatedMilestoneMaps, allObjectives, viewMode, zoomLevel]);

  switch (viewMode) {
    case 'stacked': {
      return (
        <Map3RelativesStacked
          mainMilestoneMapUuid={props.mainMilestoneMapUuid}
          relatedMilestoneMaps={relatedMilestoneMaps}
          allObjectives={props.allObjectives}
          dependencyRecords={props.dependencyRecords}
          children={props.children}
          queueResultDeadlineUpdate={props.queueResultDeadlineUpdate}
          queueDependencyAdd={props.queueDependencyAdd}
          queueDependencyRemove={props.queueDependencyRemove}
          settings={props.settings}
          objectiveDisplayMode={props.objectiveDisplayMode}
          relativesMapsData={relativesMapsData}
          updateMapState={updateMapState}
          canEdit={canEdit}
        />
      );
    }
    case 'sharedTimeline':
      if (!zoomLevel) {
        // mr-ncj5: Replace throw with a safe fallback to avoid crashing the React tree.
        // The primary defense is the !mainMap guard in Map3RelativesWrapper (which should
        // prevent reaching here with an undefined zoomLevel), but this guard handles any
        // remaining edge case. Render null rather than crash — the loading state will
        // appear again on the next render once data stabilizes.
        return null;
      }
      return (
        <Map3RelativesSharedTimeline
          mainMilestoneMapUuid={props.mainMilestoneMapUuid}
          relatedMilestoneMaps={relatedMilestoneMaps}
          allObjectives={props.allObjectives}
          dependencyRecords={props.dependencyRecords}
          children={props.children}
          queueResultDeadlineUpdate={props.queueResultDeadlineUpdate}
          queueDependencyAdd={props.queueDependencyAdd}
          queueDependencyRemove={props.queueDependencyRemove}
          settings={props.settings}
          zoomLevel={zoomLevel}
          objectiveDisplayMode={props.objectiveDisplayMode}
          relativesMapsData={relativesMapsData}
          updateMapState={updateMapState}
          canEdit={canEdit}
        />
      );
    default: {
      console.warn(`Unknown view mode: ${viewMode}, falling back to 'stacked'`);
      return (
        <Map3RelativesStacked
          mainMilestoneMapUuid={props.mainMilestoneMapUuid}
          relatedMilestoneMaps={relatedMilestoneMaps}
          allObjectives={props.allObjectives}
          dependencyRecords={props.dependencyRecords}
          children={props.children}
          queueResultDeadlineUpdate={props.queueResultDeadlineUpdate}
          queueDependencyAdd={props.queueDependencyAdd}
          queueDependencyRemove={props.queueDependencyRemove}
          settings={props.settings}
          objectiveDisplayMode={props.objectiveDisplayMode}
          relativesMapsData={relativesMapsData}
          updateMapState={updateMapState}
          canEdit={canEdit}
        />
      );
    }
  }
}

/**
 * Helper function to update the collapse state of a map.
 *
 * @returns updated relativesMapsData
 */
function updateMapState_(
  prev: RelativesMapData[],
  mapUuid: string,
  updates: UserMapCollapseControls,
  mainMilestoneMapUuid: string,
) {
  // STEP 1: Update the specific map's collapse state
  const updatedMaps = prev.map((mapData) => {
    if (mapData.milestoneMap.uuid === mapUuid) {
      // Merge user updates with existing state
      const newCollapseLevel = updates.collapseLevel ?? mapData.collapseLevel;
      const newHideRowsWithoutDependencies =
        updates.hideRowsWithoutDependencies ?? mapData.hideRowsWithoutDependencies;

      // Create updated map data with new collapse state for height calculation
      const updatedMapData = {
        ...mapData,
        collapseLevel: newCollapseLevel,
        hideRowsWithoutDependencies: newHideRowsWithoutDependencies,
      };

      // Calculate heightByRows automatically based on the NEW state
      const newHeightByRows = calculateHeightByRows(updatedMapData);

      return {
        ...updatedMapData,
        heightByRows: newHeightByRows,
      };
    }
    return mapData;
  });

  // STEP 2: Recalculate ALL Y offsets using new algorithm that handles fullyCollapsed grouping
  const newYOffsets = getMapYOffsetsWithFullyCollapsedGrouping(updatedMaps, mainMilestoneMapUuid);

  // STEP 3: Apply new Y offsets to all maps
  return updatedMaps.map((mapData, index) => ({
    ...mapData,
    mapYOffset: newYOffsets[index],
  }));
}

/**
 * Creates a MapNode (replaces Map3StaticBackground overlays).
 * Position is taken directly from RelativesMapData (mapXOffset, mapYOffset).
 */
export function createMapNode(
  relativeMapData: RelativesMapData,
  settings: Map3UISettings = {},
  zoomLevel?: MilestoneMapInterval,
  updateMapState?: (mapUuid: string, updates: UserMapCollapseControls) => void,
  mainMilestoneMapUuid?: string,
): Node<MapNodeData> {
  const { milestoneMap, objectives, columnsInfo } = relativeMapData;

  // Calculate map dimensions (same as Map3StaticBackground)
  const mapWidth =
    GRID_CONFIG.objectiveWidth +
    columnsInfo.filter((col) => isStatusColumn(col)).reduce((sum, col) => sum + col.width, 0) -
    1;

  // Use dynamic height calculation based on collapse state
  const mapHeight = calculateHeightByRows(relativeMapData) * GRID_CONFIG.cellHeight;

  return {
    id: `map-${relativeMapData.milestoneMap.uuid}`,
    type: 'mapNode',
    position: {
      x: relativeMapData.mapXOffset, // Use embedded offset directly
      y: relativeMapData.mapYOffset, // Use embedded offset directly
    },
    data: {
      milestoneMap: relativeMapData.milestoneMap,
      objectives: relativeMapData.objectives,
      columnsInfo: relativeMapData.columnsInfo,
      settings,
      zoomLevel,
      collapseLevel: relativeMapData.collapseLevel,
      hideRowsWithoutDependencies: relativeMapData.hideRowsWithoutDependencies,
      heightByRows: relativeMapData.heightByRows,
      isMainMap: mainMilestoneMapUuid === relativeMapData.milestoneMap.uuid,
      updateMapState,
    },
    // Set the node size based on the dynamic collapse state
    style: {
      width: mapWidth,
      height: mapHeight,
      zIndex:
        relativeMapData.collapseLevel !== 'expanded'
          ? GRID_CONFIG.zIndex.collapsedMaps
          : GRID_CONFIG.zIndex.maps,
    },
    draggable: false,
  };
}
